import type { Scope } from './sites';
import { z } from 'zod';

/** Processi, esecuzioni e task (Fase 1). Il contratto viaggia come sorgente YAML. */

export const ContractIssueSchema = z.object({ path: z.string(), message: z.string() });
export type ContractIssueDto = z.infer<typeof ContractIssueSchema>;

export const ContractSourceSchema = z.object({
  source: z.string().min(10).max(100_000),
  /** Da dove arriva: scritto a mano, dalla conversazione (Fase 3), dalla libreria. */
  origin: z.enum(['manuale', 'conversazione', 'libreria', 'controllo']).default('manuale'),
  /** Area della pagina Procedure del processo nuovo (obbligatoria per i responsabili delle procedure). */
  area: z.string().trim().min(1).max(120).optional(),
});
export type ContractSourceInput = z.infer<typeof ContractSourceSchema>;

/** Risposta della validazione: contratto normalizzato e procedura derivata, oppure i problemi. */
export type ContractValidation =
  | { ok: true; contract: unknown; procedure: unknown }
  | { ok: false; issues: ContractIssueDto[] };

export const PROCEDURE_STATUSES = ['proposta', 'osservazione', 'attiva', 'ritirata'] as const;
export type ProcedureStatus = (typeof PROCEDURE_STATUSES)[number];

export interface ProcessSummary {
  id: string;
  key: string;
  name: string;
  status: 'attivo' | 'sospeso';
  contract_version: number;
  procedure: { id: string; version: number; status: ProcedureStatus } | null;
  /** Una procedura proposta attende la ratifica. */
  pending_procedure_id: string | null;
  open_runs: number;
  /** Ruoli che svolgono i passi (contratto attivo): i compiti vanno a chi ha questi ruoli. */
  roles: string[];
  /** Per quali sedi vale (ADR 0017): con ambito "sedi" si esegue una volta per ogni sede. */
  ambito: Scope;
}

export interface ProcessDetail extends ProcessSummary {
  goal: string;
  contract: { id: string; version: number; source: string; parsed: unknown; origin: string; created_at: string };
  contracts: Array<{ id: string; version: number; status: string; origin: string; created_at: string }>;
  procedures: Array<{ id: string; version: number; status: ProcedureStatus; origin: string; steps: unknown; created_at: string; ratified_at: string | null }>;
  runs: RunSummary[];
  /** Area della pagina Procedure da cui è nato (null se non è nato da una procedura). */
  area?: string | null;
  /** L'utente può cambiarlo (titolare, amministratore o responsabile delle procedure della sua area). */
  can_edit?: boolean;
}

export const StartRunSchema = z.object({
  information: z.record(z.unknown()).default({}),
  /** Con una variante in osservazione: forza la versione (altrimenti il sistema alterna le due). */
  variant: z.enum(['attuale', 'osservazione']).optional(),
  /** Provenienza delle informazioni non inserite a mano: un file caricato o i dati dell'archivio (es. un software collegato). */
  provenance: z.record(z.object({ documento_id: z.string().uuid().optional(), fonte: z.string().max(200).optional() })).default({}),
  /** Sede in cui si svolge (ADR 0017): obbligatoria per un processo di più sedi, i compiti vanno a chi ci lavora. */
  site_id: z.string().uuid().optional(),
});
export type StartRunInput = z.infer<typeof StartRunSchema>;

export interface RunSummary {
  id: string;
  process_id: string;
  process_name: string;
  status: 'aperta' | 'chiusa' | 'annullata';
  /** Sede dell'esecuzione (null = tutta l'azienda). */
  site_id: string | null;
  site_name: string | null;
  started_at: string;
  closed_at: string | null;
  tasks_total: number;
  tasks_closed: number;
}

export interface TaskDto {
  id: string;
  run_id: string;
  process_id: string;
  process_name: string;
  action: string;
  item_key: string;
  item: Record<string, unknown>;
  attempt: number;
  title: string;
  role: string;
  assignee: { id: string; name: string } | null;
  status: 'aperta' | 'chiusa' | 'annullata';
  outcomes: string[];
  outcome: string | null;
  confirmation_required: boolean;
  risk_class: string;
  message: string | null;
  /** Istruzioni operative della procedura per questo passo. */
  instructions: string | null;
  note: string | null;
  created_at: string;
  closed_at: string | null;
  closed_on_behalf: boolean;
  can_close: boolean;
}

export interface RunDetail extends RunSummary {
  procedure_steps: unknown;
  information: Array<{ key: string; value: unknown; source: string; acquisition_mode: string; acquired_at: string }>;
  tasks: TaskDto[];
}

export const CloseTaskSchema = z.object({
  outcome: z.string().optional(),
  note: z.string().trim().max(2000).optional(),
  /** Obbligatorio per le azioni con conferma obbligatoria (es. invio verso l'esterno). */
  confirmed: z.boolean().optional(),
});
export type CloseTaskInput = z.infer<typeof CloseTaskSchema>;

export const AssignTaskSchema = z.object({ person_id: z.string().uuid() });
export const ReportTaskSchema = z.object({ note: z.string().trim().min(1).max(2000) });

export const TASK_SCOPES = ['mine', 'open', 'unassigned'] as const;
export type TaskScope = (typeof TASK_SCOPES)[number];

/** Avvio automatico di un processo: a cadenza oppure dopo la chiusura di un altro processo. */
export const ProcessScheduleSchema = z.object({
  attivo: z.boolean().default(true),
  tipo: z
    .enum(['cadenza', 'dopo_processo', 'evento'])
    .describe('cadenza = a giorni e ora fissi; dopo_processo = quando si chiude un’esecuzione di un altro processo; evento = quando succede qualcosa fuori (una mail, dati nuovi da un programma, una scadenza)'),
  ogni: z.enum(['giorno', 'settimana', 'mese']).optional().describe('Per cadenza'),
  giorni_settimana: z.array(z.number().int().min(1).max(7)).max(7).optional().describe('Per ogni settimana: 1 = lunedì … 7 = domenica'),
  giorno_del_mese: z.number().int().min(1).max(31).optional().describe('Per ogni mese (31 = ultimo giorno del mese)'),
  ora: z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/).default('08:00').describe('Ora di avvio, ora italiana'),
  dopo_processo_id: z.string().uuid().optional().describe('Per dopo_processo'),
  informazioni: z.record(z.unknown()).default({}).describe('Informazioni fisse con cui parte ogni esecuzione (le altre arrivano dall’archivio o dalle presenze)'),
  evento_fonte: z
    .enum(['posta', 'archivio', 'documento', 'scadenza'])
    .optional()
    .describe('Per evento: posta = arriva una mail di un certo tipo in una casella collegata; archivio = righe nuove da un programma collegato o da un file caricato; documento = viene caricato un documento di un certo tipo (anche allegati di mail e file dei programmi); scadenza = un documento obbligatorio sta per scadere'),
  evento_descrizione: z
    .string()
    .trim()
    .min(5)
    .max(500)
    .optional()
    .describe('Per posta e documento: quale mail o quale documento fa partire il processo, in parole (es. "un cliente chiede un preventivo", "un contratto firmato con un nuovo cliente")'),
  casella: z.string().trim().toLowerCase().email().optional().describe('Per posta: solo questa casella (se vuoto, tutte quelle collegate)'),
  tipo_archivio: z.string().trim().min(1).max(63).optional().describe('Per archivio: tipo di dati (da tipi_archivio); ogni riga nuova, da un programma collegato o da un file caricato (con colonna chiave), fa partire un’esecuzione'),
  campi: z.record(z.string().max(63)).optional().describe('Per archivio: informazione del processo ← attributo della riga'),
  giorni_prima: z.number().int().min(0).max(365).optional().describe('Per scadenza: quanti giorni prima della scadenza parte (default 30)'),
  voce_id: z.string().uuid().optional().describe('Per scadenza: solo questa voce dei documenti obbligatori (se vuoto, tutte)'),
  campo_evento: z.string().max(63).optional().describe('Informazione del processo che riceve la descrizione dell’evento (la mail, la riga, il documento in scadenza)'),
});
export type ProcessScheduleInput = z.infer<typeof ProcessScheduleSchema>;

export interface ProcessScheduleDto {
  processo_id: string;
  processo: string;
  attivo: boolean;
  tipo: 'cadenza' | 'dopo_processo' | 'evento';
  /** Per evento: da dove arriva e cosa lo fa scattare. */
  evento: {
    fonte: 'posta' | 'archivio' | 'documento' | 'scadenza';
    descrizione: string | null;
    casella: string | null;
    tipo_archivio: string | null;
    campi: Record<string, string>;
    giorni_prima: number | null;
    voce_id: string | null;
    campo_evento: string | null;
  } | null;
  ogni: 'giorno' | 'settimana' | 'mese' | null;
  giorni_settimana: number[];
  giorno_del_mese: number | null;
  ora: string | null;
  dopo_processo_id: string | null;
  dopo_processo: string | null;
  informazioni: Record<string, unknown>;
  /** In parole: "ogni lunedì alle 08:00", "dopo Ricezione ordine". */
  descrizione: string;
  prossimo_avvio: string | null;
  ultimo_avvio: string | null;
  ultima_esecuzione_id: string | null;
  /** Perché l'ultimo avvio non è riuscito (es. procedura non ratificata, informazione mancante). */
  ultimo_errore: string | null;
}
